import { Injectable } from '@angular/core';

import { StorageService } from './storage.service';

/**
 * Storage Service to manage storage on the browser's local storage
 * @export
 */
@Injectable({
    providedIn: 'root',
})
export class LocalStorageService implements StorageService {
    /**
     * Return the value stored on the browser's local storage, associated with the key.
     *
     * @throws The key can not be null or empty.
     */
    get<T>(key: string, isKeyEncoded?: boolean): T {
        if (!key || key.trim() === '') {
            throw new Error('The key can not be null or empty');
        }

        const value = localStorage.getItem(this.encodeKey(key, isKeyEncoded));

        if (value === undefined || value === null) {
            return null;
        }

        const parsedValue = JSON.parse(value) as T;
        if (parsedValue === undefined || parsedValue === null) {
            return null;
        }

        return parsedValue;
    }

    /**
     * Store the value on the browser's local storage, associated with the key.
     *
     * @throws The key can not be null or empty.
     * @throws The value can not be null.
     */
    set<T>(key: string, value: T, encodeKey?: boolean, errorMessage?: string): void {
        if (!key || key.trim() === '') {
            throw new Error('The key can not be null or empty');
        }
        if (value === null || value === undefined) {
            throw new Error('The value can not be null or undefined');
        }

        const jsonValue = JSON.stringify(value);

        try {
            localStorage.setItem(this.encodeKey(key, encodeKey), jsonValue);
        } catch(_) {
            try {
                // perform 2nd attempt
                localStorage.setItem(this.encodeKey(key, encodeKey), jsonValue);
            } catch(error: any) {
                // Storage full, maybe notify user or do some clean-up
                throw new Error(errorMessage ?? error?.message);
            }
        }
    }

    /**
     * Removes the value associated to the key from the browser's local storage;
     *
     * @throw The key can not be null or empty.
     */
    remove(key: string, isKeyEncoded?: boolean, errorMessage?: string): void {
        if (!key || key.trim() === '') {
            throw new Error('The key can not be null or empty');
        }

        try {
            localStorage.removeItem(this.encodeKey(key, isKeyEncoded));
        } catch(_) {
            try {
                // perform 2nd attempt
                localStorage.removeItem(this.encodeKey(key, isKeyEncoded));
            } catch(error: any) {
                // Storage full, maybe notify user or do some clean-up
                throw new Error(errorMessage ?? error?.message);
            }
        }
    }

    /**
     * Remove all the values from the browser's local storage.
     */
    clear(): void {
        localStorage.clear();
    }

    /**
     * Encodes the key using base64
     *
     * @param key String to be encoded
     * @param encode Whether the key should be encoded
     * @returns The encoded key or the original
     */
    private encodeKey(key: string, encode?: boolean): string {
        return encode ? btoa(key) : key;
    }
}
